
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import AppDropzone from "../../../Component/Common/Components/FormValidationInputs/AppDropZone";
import AppSelectList from "../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { MDAlert } from "../../../Component/Common/Components/MDAlert";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { DataImportType } from "../../../Component/Model/Enum/BEE123Enums";
import { ImportCommonFunctionsDropDown, getUploadFileName } from "../../../Helpers/ImportHelper";
import { toast } from "react-toastify";
import { ImportService } from "../../../Component/Api/Import/ImportService";
import { ImportProgressBarWithErrorList } from "../../../Component/Common/Components/ProgressBar/ImportProgressBarWithErrors";
import { ExportKeyValue } from "../../../Component/Model/Dto/Upload";
import { defaultObject, emptyGuid } from "../../../Helpers/typescriptHelper";
import { TooltipLoadingButton } from "../../../Component/Common/Components/Tooltip/TooltipLoadingButton";
import { CoreImportService } from "../../../Component/Api/Import/CoreImportService";
import {useSignalRConnection} from "../../../Hooks/useSignalRConnection";

interface IProps {
    open: boolean;
    closeModal: () => void;
    dataImportType: DataImportType;
    scorecardId?: string;
    organisationId: number;
}

export interface ProgressData {
    progress: number;
    numberOfRowsCorrect: number;
    numberOfRowsFailed: number;
    log: any[];
    isSuccess: boolean;
}

export const WSPImportModal = ({ open, closeModal, dataImportType, scorecardId, organisationId }: IProps) => {
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [importManagementTrackerId, setImportManagementTrackerId] = useState<number>(0);
    const [progressData, setProgressData] = useState<ProgressData>({
        progress: 0,
        numberOfRowsCorrect: 0,
        numberOfRowsFailed: 0,
        log: [],
        isSuccess: false,
    });
    const hub = useSignalRConnection("ImportHub");

    const { control, handleSubmit, watch, setValue } = useForm({
        mode: 'all',
    });

    const clearFile = () => {
        setValue('file', null);
    };

    useEffect(() => {
        if (importManagementTrackerId == 0) return;

        (async () => {
            try {
                if(!hub.isConnected)
                {
                    await hub.start();
                }
                hub.on(`excelImportProgress-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    setProgressData(data);
                })

                hub.on(`excelImportFailed-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    const { NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
                    setProgressData({
                        ...data,
                        progress: 0,
                        isSuccess: false,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        numberOfRowsFailed: NumberOfRowsFailed,
                        log: Log
                    });
                    toast.error("Import Failed");
                    setIsImporting(false);
                });

                hub.on(`excelImportSuccess-${organisationId}-${importManagementTrackerId}`, (data: any) => {
                    const { NumberOfRowsCorrect, Log } = data;
                    setProgressData({
                        ...data,
                        isSuccess: true,
                        progress: 100,
                        numberOfRowsCorrect: NumberOfRowsCorrect,
                        log: Log
                    });
                    toast.success("Import Succeeded");
                    setIsImporting(false);
                })
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, [importManagementTrackerId]);

    const getImportTemplate = (e: any) => {
        ImportService.DownloadImportTemplate(emptyGuid, getUploadFileName(DataImportType[dataImportType]));
    };

    const onDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [{
                    key: 'ScorecardId',
                    value: scorecardId
                },
            ];
            const request = {
                dataImportTypes:[dataImportType], 
                dataImportType: dataImportType,
                importAction: parseInt(data.importType),
                scorecardId: scorecardId ?? '',
                file: data.file,
                organisationId: organisationId,
                parameters: parameters
            }
            var result = await CoreImportService.ImportMultiPageExcelFile(request);
            setImportManagementTrackerId(result.data.importManagementTrackerId);
        } catch (error) {
            console.log(error);
            setIsImporting(false);
            toast.error('Import Failed');
        }
    }

    const file = watch('file');
    const importType = watch('importType');

    const getTooltipTitle = () => {
        if (!file && !importType) {
            return "File and Import Type is required"
        }
        else if (!file) {
            return "File is required";
        } else if (!importType) {
            return "Import Type is required";
        } else {
            return "";
        }
    }

    return (
        <Dialog open={open} onClose={() => closeModal()} maxWidth={false} aria-labelledby="form-dialog-title">
            <form
                id="JobTitleImportForm"
                onSubmit={handleSubmit(onDataSubmit)}
            >
                <DialogTitle>
                    File Upload
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeModal}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ paddingTop: "5px !important", width: "600px " }}>
                    <AppSelectList
                        disabled={isImporting}
                        control={control}
                        name="importType"
                        label="Import Type"
                        items={ImportCommonFunctionsDropDown(DataImportType[dataImportType])}
                    />
                    <Box mt={2}>
                        {!file && <AppDropzone control={control} name="file" width={568} isExcel></AppDropzone>}
                        {file && (
                            <Box display="flex" flexDirection="column" alignItems="center">
                                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                                    <Box position="relative">
                                        <img src={`${process.env.PUBLIC_URL}/img/xlsx-icon.png`} alt='preview' style={{ maxHeight: '200px' }} />
                                        <IconButton
                                            disabled={isImporting}
                                            onClick={clearFile}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                zIndex: 1
                                            }}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <MDTypography variant="caption" align="center">{file.name}</MDTypography>
                            </Box>
                        )}
                    </Box>
                    <Box mt={2}>
                        <ImportProgressBarWithErrorList
                            value={progressData?.progress ?? 0}
                            progressData={progressData}
                        />
                    </Box>
                    <Grid item xs={5}>
                        <TooltipLoadingButton
                            buttonText="Begin Import"
                            isLoading={isImporting}
                            tooltipTitle={getTooltipTitle()}
                            showTooltipOnDisabled={true}
                            form="JobTitleImportForm"
                            disabled={!file || !importType}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                        <MDBox mt={4} color="white">
                            <MDAlert color='light'>
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                                    <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" size="small" onClick={getImportTemplate} style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                        to download the template file</p>
                                </MDTypography>
                            </MDAlert>
                        </MDBox>
                    </Grid>
                </DialogContent>
            </form>
        </Dialog>
    )
}